'use client';

import Image from 'next/image';
import { useCallback, useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { extractCategoriesFromMap } from '@/app/utils/extractCategoriesFromMap';
import PodcastPlayer from '@/components/podcast/PodcastPlayer';
import { DotBlockEditor } from '@/components/shared/decodeTextBlock';
import SocialMediaShare from '@/components/shared/socialMediaShare';
import PodcastCard from '@/components/podcast/PodcastCard';
import Contentlet from '@/components/shared/contentlet';
import YouTubeEmbed from '@/components/shared/YouTubeEmbed';
import { cn } from '@/app/utils/cn';
import { getThumbSrc } from '@/app/utils/podcastUtils';
import Author from '@/components/shared/author';

function PlatformButton({ href, gradient, border, borderHover, shadow, shadowHover, children }) {
    const [hovered, setHovered] = useState(false);
    if (!href) return null;
    return (
        <a
            href={href || '#'}
            target="_blank"
            rel="noopener noreferrer"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            className="flex flex-1 items-center justify-center gap-2 rounded-2xl border px-4 py-3 text-sm font-medium text-white backdrop-blur-md"
            style={{
                backgroundImage: gradient,
                backgroundSize: '200% 200%',
                backgroundPosition: hovered ? '100% 100%' : '0% 0%',
                borderColor: hovered ? borderHover : border,
                boxShadow: hovered ? shadowHover : shadow,
                transform: hovered ? 'translateY(-2px)' : 'translateY(0)',
                transition:
                    'background-position 0.5s ease, border-color 0.3s ease, box-shadow 0.3s ease, transform 0.2s ease'
            }}>
            {children}
        </a>
    );
}

export default function PodcastDetailPage({ podcast, relatedPodcasts = [], maxLimit = 4 }) {
    const pathname = usePathname();
    const [url, setUrl] = useState('');
    const [isPlaying, setIsPlaying] = useState(false);
    const [ytDuration, setYtDuration] = useState('');

    const src = getThumbSrc(podcast.thumbnail);
    const youtubeUrl = podcast.youtubeUrl || '';
    const isYoutube = !!youtubeUrl;
    const mediaUrl = podcast.identifier ? `/dA/${podcast.identifier}` : null;
    const canPlay = isYoutube || !!mediaUrl;
    const title = podcast.title?.replace(/\.[^/.]+$/, '') || '';
    const authors = Array.isArray(podcast.author) ? podcast.author : [];
    const tags = podcast.tags
        ? podcast.tags
              .split(',')
              .map((t) => t.trim())
              .filter(Boolean)
        : [];
    const categories = extractCategoriesFromMap(podcast.categories) ?? [];
    const formattedDate = podcast.publishDate
        ? new Date(podcast.publishDate).toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric'
          })
        : null;

    useEffect(() => {
        setUrl(new URL(pathname, window.location.origin).toString());
    }, [pathname]);

    const handleYtDuration = useCallback((d) => setYtDuration(d), []);

    const displayDuration = podcast.duration || ytDuration;

    return (
        <Contentlet contentlet={podcast}>
            <main className="flex w-full flex-col">
                {/* ── Hero ─────────────────────────────────────────────────────── */}
                <section className="relative w-full px-5 pb-14 pt-10">
                    {/* Ambient glow — overflow-hidden lives here so it never clips the flex reflow */}
                    <div
                        className="pointer-events-none absolute inset-0 overflow-hidden"
                        aria-hidden="true">
                        <div
                            className="absolute inset-0"
                            style={{
                                background:
                                    'radial-gradient(ellipse 700px 500px at 15% 60%, rgba(182,38,232,0.12) 0%, transparent 70%)'
                            }}
                        />
                    </div>

                    <div className="container relative p-0">
                        {/*
                         * flex-wrap is the engine of the play animation.
                         * Default: media is ~420px wide, metadata fills the rest of the row.
                         * Playing: media grows to 100% width, metadata wraps to the row below.
                         */}
                        <div
                            className={cn(
                                'flex flex-wrap items-start gap-10 lg:gap-12',
                                isPlaying ? 'w-[90%] mx-auto' : ''
                            )}>
                            {/* ── Media ── */}
                            <div
                                className="shrink-0"
                                style={{
                                    width: '100%',
                                    maxWidth: isPlaying ? '100%' : '420px',
                                    transition: 'max-width 0.7s cubic-bezier(0.4, 0, 0.2, 1)'
                                }}>
                                {/* 16:9 always — padding-bottom trick for reliable cross-browser aspect ratio */}
                                <div
                                    className="relative w-full overflow-hidden rounded-3xl bg-gradient-to-br from-fuschia-700 via-fuschia-900 to-blue-700"
                                    style={{
                                        paddingBottom: '56.25%',
                                        boxShadow:
                                            '0 24px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.06)'
                                    }}>
                                    {/* Artwork */}
                                    {src && (
                                        <Image
                                            src={src}
                                            alt={podcast.thumbnailAlt || title}
                                            fill
                                            priority
                                            className="object-cover"
                                            sizes="(max-width: 1024px) 100vw, (max-width: 1280px) 420px, 1200px"
                                            style={{
                                                opacity: isPlaying ? 0 : 1,
                                                transition: 'opacity 0.4s ease'
                                            }}
                                        />
                                    )}

                                    {/* Gradient overlay on artwork */}
                                    <div
                                        className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"
                                        style={{
                                            opacity: isPlaying ? 0 : 1,
                                            transition: 'opacity 0.4s ease'
                                        }}
                                    />

                                    {/* Play button */}
                                    {canPlay && !isPlaying && (
                                        <button
                                            onClick={() => setIsPlaying(true)}
                                            className="group absolute inset-0 flex items-center justify-center"
                                            aria-label={isYoutube ? 'Play video' : 'Play episode'}>
                                            <span className="flex size-16 items-center justify-center rounded-full bg-white/20 ring-1 ring-white/40 backdrop-blur-sm transition-transform duration-200 group-hover:scale-110 group-hover:bg-white/30">
                                                <svg
                                                    width="26"
                                                    height="26"
                                                    viewBox="0 0 24 24"
                                                    fill="white"
                                                    aria-hidden="true">
                                                    <path d="M8 5v14l11-7z" />
                                                </svg>
                                            </span>
                                        </button>
                                    )}

                                    {/* Player — fades in after layout expansion */}
                                    <div
                                        className="absolute inset-0"
                                        style={{
                                            opacity: isPlaying ? 1 : 0,
                                            transition: 'opacity 0.45s ease 0.35s',
                                            pointerEvents: isPlaying ? 'auto' : 'none'
                                        }}>
                                        {isPlaying &&
                                            (isYoutube ? (
                                                <YouTubeEmbed
                                                    url={youtubeUrl}
                                                    className="size-full"
                                                    onDuration={handleYtDuration}
                                                    autoPlay
                                                />
                                            ) : (
                                                <PodcastPlayer
                                                    identifier={podcast.identifier}
                                                    vtt={podcast.vtt}
                                                    autoPlay
                                                />
                                            ))}
                                    </div>
                                </div>
                                {/* Platform buttons */}
                                {(podcast.spotifyUrl || podcast.appleUrl) && (
                                    <div className="mt-4 flex gap-3">
                                        <PlatformButton
                                            href={podcast.spotifyUrl}
                                            gradient="linear-gradient(135deg, rgba(29,185,84,0.35) 0%, rgba(29,185,84,0.06) 50%, rgba(100,255,150,0.32) 100%)"
                                            border="rgba(29,185,84,0.3)"
                                            borderHover="rgba(29,185,84,0.75)"
                                            shadow="0 4px 20px rgba(29,185,84,0.12), inset 0 1px 0 rgba(255,255,255,0.07)"
                                            shadowHover="0 8px 35px rgba(29,185,84,0.45), inset 0 1px 0 rgba(255,255,255,0.15)">
                                            <svg
                                                width="16"
                                                height="16"
                                                viewBox="0 0 24 24"
                                                aria-hidden="true"
                                                fill="#1DB954">
                                                <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
                                            </svg>
                                            Spotify
                                        </PlatformButton>
                                        <PlatformButton
                                            href={podcast.appleUrl}
                                            gradient="linear-gradient(135deg, rgba(230,75,248,0.35) 0%, rgba(130,43,193,0.06) 50%, rgba(230,75,248,0.32) 100%)"
                                            border="rgba(230,75,248,0.3)"
                                            borderHover="rgba(230,75,248,0.75)"
                                            shadow="0 4px 20px rgba(230,75,248,0.12), inset 0 1px 0 rgba(255,255,255,0.07)"
                                            shadowHover="0 8px 35px rgba(230,75,248,0.45), inset 0 1px 0 rgba(255,255,255,0.15)">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                id="Livello_1"
                                                width="18"
                                                height="18"
                                                x="0"
                                                y="0"
                                                enableBackground="new 0 0 300 300"
                                                viewBox="0 0 300 300">
                                                <g id="XMLID_632_">
                                                    <linearGradient
                                                        id="XMLID_2_"
                                                        x1="150"
                                                        x2="150"
                                                        y1="0"
                                                        y2="300"
                                                        gradientUnits="userSpaceOnUse">
                                                        <stop
                                                            offset="0"
                                                            style={{ stopColor: '#f452ff' }}
                                                        />
                                                        <stop
                                                            offset="1"
                                                            style={{ stopColor: '#832bc1' }}
                                                        />
                                                    </linearGradient>
                                                    <path
                                                        id="XMLID_662_"
                                                        fill="url(#XMLID_2_)"
                                                        fillRule="evenodd"
                                                        d="M294.1 260c-2.9 7.4-9.6 17.8-19.2 25.2-5.5 4.2-12.1 8.3-21.1 11-9.6 2.9-21.5 3.8-36.3 3.8h-135c-14.8 0-26.6-1-36.3-3.8-9-2.7-15.6-6.7-21.1-11-9.5-7.3-16.3-17.8-19.2-25.2C.1 245.1 0 228.2 0 217.5v-135C0 71.8.1 54.9 5.9 40c2.9-7.4 9.6-17.8 19.2-25.2 5.5-4.2 12.1-8.3 21.1-11C55.9 1 67.7 0 82.5 0h135c14.8 0 26.6 1 36.3 3.8 9 2.7 15.6 6.7 21.1 11 9.5 7.3 16.3 17.8 19.2 25.2 5.9 14.9 5.9 31.9 5.9 42.5v135c0 10.7-.1 27.6-5.9 42.5"
                                                        clipRule="evenodd"
                                                    />
                                                    <g fill="#fff">
                                                        <path
                                                            id="XMLID_657_"
                                                            d="M175.7 181.1c-.4-3.6-1.6-6.2-4-8.6-4.5-4.7-12.4-7.8-21.7-7.8s-17.2 3-21.7 7.8c-2.3 2.5-3.6 5-4 8.6-.8 7-.3 13 .5 22.7.8 9.2 2.3 21.5 4.2 33.9 1.4 8.9 2.5 13.7 3.5 17.1 1.7 5.6 7.8 10.4 17.5 10.4s15.9-4.9 17.5-10.4c1-3.4 2.1-8.2 3.5-17.1 1.9-12.5 3.4-24.7 4.2-33.9.9-9.7 1.3-15.7.5-22.7"
                                                        />
                                                        <path
                                                            id="XMLID_655_"
                                                            d="M174.6 130.1c0 13.6-11 24.6-24.6 24.6s-24.6-11-24.6-24.6 11-24.6 24.6-24.6 24.6 11.1 24.6 24.6"
                                                        />
                                                        <path
                                                            id="XMLID_653_"
                                                            d="M149.7 33.2C92.3 33.4 45.3 80 44.5 137.4c-.6 46.5 29.1 86.3 70.6 100.9 1 .4 2-.5 1.9-1.5-.5-3.6-1.1-7.2-1.5-10.8-.2-1.3-1-2.3-2.1-2.9-32.8-14.3-55.7-47.2-55.3-85.3.5-50 41.3-90.7 91.2-91.1 51.1-.4 92.8 41 92.8 92 0 37.7-22.8 70.1-55.3 84.4-1.2.5-2 1.6-2.1 2.9l-1.5 10.8c-.2 1.1.9 1.9 1.9 1.5 41.1-14.4 70.6-53.6 70.6-99.6-.2-58.2-47.7-105.6-106-105.5"
                                                        />
                                                        <path
                                                            id="XMLID_651_"
                                                            d="M147.3 68.2c-37.4 1.4-67.4 32.3-67.9 69.7-.3 24.6 12 46.4 30.9 59.3.9.6 2.2-.1 2.2-1.2-.3-4.3-.3-8.1-.1-12.1.1-1.3-.4-2.5-1.4-3.4-11.5-10.8-18.5-26.2-18.1-43.2.8-30 24.9-54.4 54.9-55.6 32.6-1.3 59.4 24.9 59.4 57.1 0 16.4-7 31.2-18.1 41.7-.9.9-1.4 2.1-1.4 3.4.2 3.9.1 7.7-.1 12-.1 1.1 1.2 1.9 2.2 1.2 18.6-12.7 30.9-34.2 30.9-58.4.1-39.8-33.2-72.1-73.4-70.5"
                                                        />
                                                    </g>
                                                </g>
                                            </svg>
                                            Apple Podcasts
                                        </PlatformButton>
                                    </div>
                                )}
                            </div>

                            {/* ── Metadata ── wraps below the player when isPlaying */}
                            <div
                                className="flex min-w-0 flex-col gap-5"
                                style={{ flex: '1 1 280px' }}>
                                {/* Badge row: episode type + categories */}
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="flex w-fit items-center gap-2 rounded-full border border-white/20 bg-white/5 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white/80">
                                        <svg
                                            width="13"
                                            height="13"
                                            viewBox="0 0 24 24"
                                            fill="currentColor"
                                            aria-hidden="true">
                                            <path d="M12 3a9 9 0 0 0-9 9v7a2 2 0 0 0 2 2h3v-8H5v-1a7 7 0 0 1 14 0v1h-3v8h3a2 2 0 0 0 2-2v-7a9 9 0 0 0-9-9" />
                                        </svg>
                                        Episode
                                    </span>
                                    {categories.slice(0, 2).map((cat) => (
                                        <span
                                            key={cat.name}
                                            className="rounded-full border border-fuschia/50 bg-fuschia/15 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white/90">
                                            {cat.name}
                                        </span>
                                    ))}
                                </div>

                                {/* Title — shrinks when playing */}
                                <h1
                                    className="font-bold leading-tight text-white"
                                    style={{
                                        fontSize: isPlaying
                                            ? 'clamp(1.375rem, 2.5vw, 1.875rem)'
                                            : 'clamp(2.25rem, 4vw, 3rem)',
                                        transition: 'font-size 0.55s cubic-bezier(0.4, 0, 0.2, 1)'
                                    }}>
                                    {title}
                                </h1>

                                {/* Authors — always visible */}
                                {authors.length > 0 && (
                                    <div className="flex flex-wrap gap-4">
                                        {authors.map((author, i) => (
                                            <Author
                                                key={author.identifier || i}
                                                author={author}
                                                isDark={true}
                                                priority={false}
                                            />
                                        ))}
                                    </div>
                                )}

                                {/* Date + Duration — always visible */}
                                {(formattedDate || displayDuration) && (
                                    <div className="flex flex-wrap items-center gap-5 text-sm text-white/40">
                                        {formattedDate && (
                                            <span className="flex items-center gap-1.5">
                                                <svg
                                                    width="13"
                                                    height="13"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    aria-hidden="true">
                                                    <rect
                                                        x="3"
                                                        y="4"
                                                        width="18"
                                                        height="18"
                                                        rx="2"
                                                        ry="2"
                                                    />
                                                    <line x1="16" y1="2" x2="16" y2="6" />
                                                    <line x1="8" y1="2" x2="8" y2="6" />
                                                    <line x1="3" y1="10" x2="21" y2="10" />
                                                </svg>
                                                {formattedDate}
                                            </span>
                                        )}
                                        {displayDuration && (
                                            <span className="flex items-center gap-1.5">
                                                <svg
                                                    width="13"
                                                    height="13"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    aria-hidden="true">
                                                    <circle cx="12" cy="12" r="10" />
                                                    <polyline points="12 6 12 12 16 14" />
                                                </svg>
                                                {displayDuration}
                                            </span>
                                        )}
                                    </div>
                                )}

                                {/* Tags — collapses when playing */}
                                {tags.length > 0 && (
                                    <div
                                        style={{
                                            maxHeight: '6rem',
                                            opacity: 1,
                                            overflow: 'hidden',
                                            transition:
                                                'max-height 0.5s ease 0.05s, opacity 0.35s ease'
                                        }}>
                                        <div className="flex flex-wrap gap-2">
                                            {tags.slice(0, 6).map((tag) => (
                                                <span
                                                    key={tag}
                                                    className="rounded-full border border-white/20 bg-white/5 px-3 py-1 text-xs font-medium text-white/60 transition-colors hover:border-white/30 hover:text-white/80">
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Share — always visible */}
                                {url && (
                                    <div className="flex items-center gap-3 pt-5">
                                        <span className="text-sm text-white/40">Share:</span>
                                        <SocialMediaShare url={url} />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </section>

                {/* ── Show Notes ──────────────────────────────────────────────── */}
                {podcast.description && (
                    <section className="border-t border-white/10 px-5 py-12 lg:py-16">
                        <div className="container p-0">
                            <div className="mx-auto max-w-3xl">
                                <h2 className="mb-8 text-2xl font-bold text-white">Show Notes</h2>
                                <article className="prose w-full max-w-fit">
                                    <DotBlockEditor blocks={podcast.description} />
                                </article>
                            </div>
                        </div>
                    </section>
                )}

                {/* ── More Episodes ───────────────────────────────────────────── */}
                {relatedPodcasts.length > 0 && (
                    <section className="border-t border-white/10 px-5 py-12 lg:py-16">
                        <div className="container p-0">
                            <h3 className="mb-8 text-2xl font-bold text-white">More Episodes</h3>
                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                                {relatedPodcasts.slice(0, maxLimit).map((p) => (
                                    <PodcastCard key={p.identifier} podcast={p} />
                                ))}
                            </div>
                        </div>
                    </section>
                )}
            </main>
        </Contentlet>
    );
}
